import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API, getAuthHeaders, handleApiError } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { 
  TrendingUp, TrendingDown, Wallet, ChevronRight, 
  ShoppingCart, CreditCard, FileText, RefreshCw, CheckCircle2,
  AlertCircle, Plus, MessageCircle, Phone, User, Send, Search, X,
  Calendar, ChevronLeft, ArrowLeft, Printer
} from 'lucide-react';
import { toast } from 'sonner';
import { usePrintShortcut, generatePrintContent, openPrintWindow, formatPrintCurrency, formatPrintDate } from '../../utils/printUtils';

// Month Selector Component
const MonthSelector = ({ value, onChange }) => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const [year, month] = value.split('-').map(Number);
  const monthName = months[month - 1];
  
  const goToPreviousMonth = () => {
    if (month === 1) {
      onChange(`${year - 1}-12`);
    } else {
      onChange(`${year}-${String(month - 1).padStart(2, '0')}`);
    }
  };
  
  const goToNextMonth = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    
    // Don't go beyond current month
    if (year > currentYear || (year === currentYear && month >= currentMonth)) {
      return;
    }
    
    if (month === 12) {
      onChange(`${year + 1}-01`);
    } else {
      onChange(`${year}-${String(month + 1).padStart(2, '0')}`);
    }
  };
  
  const isCurrentMonth = () => {
    const now = new Date();
    return year === now.getFullYear() && month === now.getMonth() + 1;
  };
  
  return (
    <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-1.5 py-1">
      <button
        onClick={goToPreviousMonth}
        className="p-1 hover:bg-slate-100 rounded transition-colors"
      >
        <ChevronLeft className="h-4 w-4 text-slate-600" />
      </button>
      <div className="flex items-center gap-1 min-w-[120px] justify-center">
        <Calendar className="h-3.5 w-3.5 text-blue-500" />
        <span className="text-xs font-medium text-slate-700">{monthName} {year}</span>
      </div>
      <button
        onClick={goToNextMonth}
        disabled={isCurrentMonth()}
        className={`p-1 rounded transition-colors ${
          isCurrentMonth() ? 'text-slate-300 cursor-not-allowed' : 'hover:bg-slate-100 text-slate-600'
        }`}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
};

const KhataBook = () => {
  const [activeTab, setActiveTab] = useState('payables');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  
  // Month filter
  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  
  // Payables state
  const [distributors, setDistributors] = useState([]);
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [distributorLedger, setDistributorLedger] = useState(null);
  
  // Receivables state
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerLedger, setCustomerLedger] = useState(null);
  
  // Summary state
  const [payableSummary, setPayableSummary] = useState({ total: 0, purchases: 0, payments: 0, monthPurchases: 0, monthPayments: 0 });
  const [receivableSummary, setReceivableSummary] = useState({ total: 0, sales: 0, received: 0, monthSales: 0, monthReceived: 0 });
  
  const [loading, setLoading] = useState(true);
  const [loadingLedger, setLoadingLedger] = useState(false);
  
  // Dialog states
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [showAddSale, setShowAddSale] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  
  // Form states
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '' });
  const [newSale, setNewSale] = useState({ bill_number: '', amount: '', date: new Date().toISOString().split('T')[0], notes: '' });
  const [newPayment, setNewPayment] = useState({ amount: '', date: new Date().toISOString().split('T')[0], payment_mode: 'cash', notes: '' });

  useEffect(() => {
    fetchData();
  }, [selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [distRes, custRes, recSumRes] = await Promise.all([
        axios.get(`${API}/distributors-outstanding?month=${selectedMonth}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/customers-receivable?month=${selectedMonth}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/receivables/summary?month=${selectedMonth}`, { headers: getAuthHeaders() })
      ]);
      
      setDistributors(distRes.data);
      setCustomers(custRes.data);
      setReceivableSummary({
        total: recSumRes.data.total_receivable,
        sales: recSumRes.data.total_sales,
        received: recSumRes.data.total_received,
        monthSales: recSumRes.data.month_sales || 0,
        monthReceived: recSumRes.data.month_received || 0
      });
      
      // Calculate payable summary including month-specific data
      const payTotals = distRes.data.reduce((acc, d) => ({
        total: acc.total + (d.outstanding || 0),
        purchases: acc.purchases + (d.total_purchases || 0),
        payments: acc.payments + (d.total_payments || 0),
        monthPurchases: acc.monthPurchases + (d.month_purchases || 0),
        monthPayments: acc.monthPayments + (d.month_payments || 0)
      }), { total: 0, purchases: 0, payments: 0, monthPurchases: 0, monthPayments: 0 });
      setPayableSummary(payTotals);
      
    } catch (error) {
      const message = handleApiError(error, 'Failed to fetch data');
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Distributor Ledger
  const fetchDistributorLedger = async (distributorId) => {
    setLoadingLedger(true);
    try {
      const response = await axios.get(`${API}/party-ledger/${distributorId}`, { headers: getAuthHeaders() });
      setDistributorLedger(response.data);
    } catch (error) {
      toast.error('Failed to fetch ledger');
    } finally {
      setLoadingLedger(false);
    }
  };

  // Customer Ledger
  const fetchCustomerLedger = async (customerId) => {
    setLoadingLedger(true);
    try {
      const response = await axios.get(`${API}/customer-ledger/${customerId}`, { headers: getAuthHeaders() });
      setCustomerLedger(response.data);
    } catch (error) {
      toast.error('Failed to fetch ledger');
    } finally {
      setLoadingLedger(false);
    }
  };

  const handleDistributorClick = (distributor) => {
    setSelectedDistributor(distributor);
    fetchDistributorLedger(distributor.id);
  };

  const handleCustomerClick = (customer) => {
    setSelectedCustomer(customer);
    fetchCustomerLedger(customer.id);
  };

  const handleBackToList = () => {
    setSelectedDistributor(null);
    setDistributorLedger(null);
    setSelectedCustomer(null);
    setCustomerLedger(null);
    fetchData();
  };

  // Print function for Khatabook Ledger
  const handlePrintLedger = useCallback(() => {
    if (!distributorLedger) return;

    const headers = [
      { label: 'Date', align: 'left' },
      { label: 'Entry Type', align: 'center' },
      { label: 'Bill No. / Ref', align: 'left' },
      { label: 'Purchase (+)', align: 'right' },
      { label: 'Payment (-)', align: 'right' },
      { label: 'Balance', align: 'right' }
    ];

    const rows = (distributorLedger.entries || []).map(entry => [
      formatPrintDate(entry.date),
      `<span class="badge ${entry.entry_label === 'Purchase' ? 'badge-blue' : 'badge-green'}">${entry.entry_label || 'Entry'}</span>`,
      entry.reference || entry.notes || '-',
      (entry.credit || 0) > 0 
        ? `<span class="amount-positive">+${formatPrintCurrency(entry.credit)}</span>` 
        : '-',
      (entry.debit || 0) > 0 
        ? `<span class="amount-negative">-${formatPrintCurrency(entry.debit)}</span>` 
        : '-',
      `<span class="${(entry.running_balance || 0) > 0 ? 'amount-negative' : 'amount-positive'}">${formatPrintCurrency(Math.abs(entry.running_balance || 0))}</span>`
    ]);

    const summary = [
      { label: 'Total Purchases', value: formatPrintCurrency(distributorLedger.total_purchases || 0), type: 'neutral' },
      { label: 'Total Payments', value: formatPrintCurrency(distributorLedger.total_payments || 0), type: 'positive' },
      { label: 'Outstanding Balance', value: formatPrintCurrency(distributorLedger.outstanding_balance || 0), type: 'negative' }
    ];

    const content = generatePrintContent({
      title: `Ledger - ${distributorLedger.party_name || 'Distributor'}`,
      businessName: 'Shri Kripa Pharmacy',
      dateRange: `As of ${new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`,
      headers,
      rows,
      summary
    });

    openPrintWindow(content);
    toast.success('Opening print preview...');
  }, [distributorLedger]);

  // Print function for main Khatabook view
  const handlePrintKhatabook = useCallback(() => {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const [year, month] = selectedMonth.split('-').map(Number);
    const dateRange = `${months[month - 1]} ${year}`;

    const headers = [
      { label: 'Party Name', align: 'left' },
      { label: 'Type', align: 'center' },
      { label: 'Transactions', align: 'center' },
      { label: 'Outstanding', align: 'right' }
    ];

    const allParties = activeTab === 'payables' 
      ? distributors.filter(d => d.outstanding > 0 || d.transactions_count > 0)
      : customers.filter(c => c.outstanding > 0 || c.transactions_count > 0);

    const rows = allParties.map(p => [
      p.name,
      `<span class="badge ${activeTab === 'payables' ? 'badge-blue' : 'badge-green'}">${activeTab === 'payables' ? 'Distributor' : 'Customer'}</span>`,
      p.transactions_count?.toString() || '0',
      `<span class="${p.outstanding > 0 ? 'amount-negative' : 'amount-positive'}">${formatPrintCurrency(Math.abs(p.outstanding || 0))}</span>`
    ]);

    const total = allParties.reduce((sum, p) => sum + (p.outstanding || 0), 0);
    const summary = [
      { label: activeTab === 'payables' ? 'Total Payables' : 'Total Receivables', value: formatPrintCurrency(total), type: activeTab === 'payables' ? 'negative' : 'positive' },
      { label: 'Total Parties', value: allParties.length.toString(), type: 'neutral' }
    ];

    const content = generatePrintContent({
      title: activeTab === 'payables' ? 'Khata Book - Payables (Distributors)' : 'Khata Book - Receivables (Customers)',
      businessName: 'Shri Kripa Pharmacy',
      dateRange,
      headers,
      rows,
      summary
    });

    openPrintWindow(content);
    toast.success('Opening print preview...');
  }, [activeTab, distributors, customers, selectedMonth]);

  // Register Alt+P shortcut - use ledger print if viewing ledger, else main view
  const handlePrint = useCallback(() => {
    if (selectedDistributor && distributorLedger) {
      handlePrintLedger();
    } else {
      handlePrintKhatabook();
    }
  }, [selectedDistributor, distributorLedger, handlePrintLedger, handlePrintKhatabook]);

  usePrintShortcut(handlePrint);

  // Add Customer
  const handleAddCustomer = async (e) => {
    e.preventDefault();
    if (!newCustomer.name || !newCustomer.phone) {
      toast.error('Name and Mobile are required');
      return;
    }
    
    try {
      await axios.post(`${API}/parties`, {
        name: newCustomer.name,
        phone: newCustomer.phone,
        type: 'customer'
      }, { headers: getAuthHeaders() });
      
      toast.success('Customer added');
      setShowAddCustomer(false);
      setNewCustomer({ name: '', phone: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add customer');
    }
  };

  // Add Sale to customer
  const handleAddSale = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/receivables/sale`, null, {
        headers: getAuthHeaders(),
        params: {
          customer_id: selectedCustomer.id,
          customer_name: selectedCustomer.name,
          amount: parseFloat(newSale.amount),
          bill_number: newSale.bill_number,
          date: newSale.date,
          notes: newSale.notes
        }
      });
      
      toast.success('Sale entry added');
      setShowAddSale(false);
      setNewSale({ bill_number: '', amount: '', date: new Date().toISOString().split('T')[0], notes: '' });
      fetchCustomerLedger(selectedCustomer.id);
    } catch (error) {
      toast.error('Failed to add sale');
    }
  };

  // Add Payment Received
  const handleAddPaymentReceived = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/receivables/payment`, null, {
        headers: getAuthHeaders(),
        params: {
          customer_id: selectedCustomer.id,
          customer_name: selectedCustomer.name,
          amount: parseFloat(newPayment.amount),
          date: newPayment.date,
          payment_mode: newPayment.payment_mode,
          notes: newPayment.notes
        }
      });
      
      toast.success('Payment received added');
      setShowAddPayment(false);
      setNewPayment({ amount: '', date: new Date().toISOString().split('T')[0], payment_mode: 'cash', notes: '' });
      fetchCustomerLedger(selectedCustomer.id);
    } catch (error) {
      toast.error('Failed to add payment');
    }
  };

  // WhatsApp Reminder
  const sendWhatsAppReminder = (customer, amount) => {
    const phone = customer.phone?.replace(/\D/g, '');
    if (!phone) {
      toast.error('No phone number available');
      return;
    }
    
    const message = `Hello ${customer.name}, you have an outstanding of ₹${amount.toLocaleString('en-IN')}. Please clear the payment. Thank you - Shri Kripa Pharmacy`;
    const whatsappUrl = `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-slate-500">Loading...</div>
        </div>
      </Layout>
    );
  }

  // ====== DISTRIBUTOR LEDGER VIEW ======
  if (selectedDistributor && distributorLedger) {
    return (
      <Layout>
        <div className="space-y-4 max-w-6xl mx-auto" data-testid="distributor-ledger-view">
          {/* Header with Back Button */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="outline" 
                onClick={handleBackToList} 
                className="h-9 px-3"
                data-testid="back-to-list-btn"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />Back to List
              </Button>
              <div>
                <h1 className="text-2xl font-heading tracking-tight text-slate-900" data-testid="distributor-name">
                  {distributorLedger.party_name || 'Distributor'}
                </h1>
                <p className="text-sm text-slate-500">Complete Ledger View</p>
              </div>
            </div>
            <Button onClick={handlePrintLedger} variant="outline" className="h-9" title="Print Ledger (Alt+P)">
              <Printer className="h-4 w-4 mr-1.5" />Print Ledger
            </Button>
          </div>

          {/* Outstanding Summary Card */}
          <Card className="p-5 border-2 border-red-200 bg-gradient-to-r from-red-50 to-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-red-100 rounded-xl">
                  <Wallet className="h-8 w-8 text-red-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-red-600 uppercase tracking-wide">Total Outstanding</p>
                  <p className="text-4xl font-bold text-red-700" data-testid="outstanding-amount">
                    ₹{(distributorLedger.outstanding_balance || 0).toLocaleString('en-IN')}
                  </p>
                </div>
              </div>
              <div className="flex gap-8">
                <div className="text-right">
                  <p className="text-xs text-slate-500 uppercase mb-1">Total Purchases</p>
                  <p className="text-2xl font-semibold text-blue-600">
                    ₹{(distributorLedger.total_purchases || 0).toLocaleString('en-IN')}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500 uppercase mb-1">Total Payments</p>
                  <p className="text-2xl font-semibold text-green-600">
                    ₹{(distributorLedger.total_payments || 0).toLocaleString('en-IN')}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Transaction History Table */}
          <Card className="border-slate-200 overflow-hidden">
            <div className="p-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Transaction History
              </h3>
              <span className="text-sm text-slate-500 bg-white px-2 py-1 rounded-lg border">
                {distributorLedger.entries_count || 0} entries
              </span>
            </div>
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full">
                <thead className="bg-slate-100 border-b border-slate-200 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase w-24">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase w-28">Entry Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Bill No. / Ref</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-blue-600 uppercase w-32">Purchase (+)</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-green-600 uppercase w-32">Payment (-)</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase w-36">Running Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(!distributorLedger.entries || distributorLedger.entries.length === 0) ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                        <FileText className="h-10 w-10 mx-auto mb-3 text-slate-300" />
                        <p className="font-medium">No transactions yet</p>
                        <p className="text-sm text-slate-400">Add purchases from Sale/Purchase module</p>
                      </td>
                    </tr>
                  ) : (
                    distributorLedger.entries.map((entry, idx) => (
                      <tr key={entry.id || idx} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {entry.date ? new Date(entry.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '-'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                            entry.entry_label === 'Purchase' 
                              ? 'bg-blue-100 text-blue-700 border border-blue-200' 
                              : 'bg-green-100 text-green-700 border border-green-200'
                          }`}>
                            {entry.entry_label === 'Purchase' ? <ShoppingCart className="h-3 w-3" /> : <CreditCard className="h-3 w-3" />}
                            {entry.entry_label || 'Entry'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-700 font-medium">
                          {entry.reference || entry.notes || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          {(entry.credit || 0) > 0 ? (
                            <span className="font-semibold text-blue-600">+₹{(entry.credit || 0).toLocaleString('en-IN')}</span>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          {(entry.debit || 0) > 0 ? (
                            <span className="font-semibold text-green-600">-₹{(entry.debit || 0).toLocaleString('en-IN')}</span>
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          <span className={`font-bold ${(entry.running_balance || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            ₹{Math.abs(entry.running_balance || 0).toLocaleString('en-IN')}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  // ====== CUSTOMER LEDGER VIEW (RECEIVABLES) ======
  if (selectedCustomer && customerLedger) {
    return (
      <Layout>
        <div className="space-y-4 max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={handleBackToList} className="h-9 px-3">
                <ArrowLeft className="h-4 w-4 mr-1" />Back
              </Button>
              <div>
                <h1 className="text-2xl font-heading tracking-tight text-slate-900">
                  {customerLedger.customer_name || 'Customer'}
                </h1>
                <p className="text-sm text-slate-500 flex items-center gap-1">
                  <Phone className="h-3 w-3" />{customerLedger.phone || 'No phone'}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Dialog open={showAddSale} onOpenChange={setShowAddSale}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white h-9">
                    <Plus className="h-4 w-4 mr-1" />Add Sale
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-sm">
                  <DialogHeader>
                    <DialogTitle>Add Sale Entry</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddSale} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-sm">Bill Number</Label>
                        <Input
                          value={newSale.bill_number}
                          onChange={(e) => setNewSale({ ...newSale, bill_number: e.target.value })}
                          placeholder="INV-001"
                          required
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm">Amount (₹)</Label>
                        <Input
                          type="number"
                          value={newSale.amount}
                          onChange={(e) => setNewSale({ ...newSale, amount: e.target.value })}
                          placeholder="0.00"
                          required
                          className="h-9"
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm">Date</Label>
                      <Input
                        type="date"
                        value={newSale.date}
                        onChange={(e) => setNewSale({ ...newSale, date: e.target.value })}
                        required
                        className="h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm">Notes</Label>
                      <Input
                        value={newSale.notes}
                        onChange={(e) => setNewSale({ ...newSale, notes: e.target.value })}
                        placeholder="Optional"
                        className="h-9"
                      />
                    </div>
                    <Button type="submit" className="w-full h-9 bg-blue-600 hover:bg-blue-700 text-white">
                      Add Sale
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              
              <Dialog open={showAddPayment} onOpenChange={setShowAddPayment}>
                <DialogTrigger asChild>
                  <Button className="bg-green-600 hover:bg-green-700 text-white h-9">
                    <Plus className="h-4 w-4 mr-1" />Payment Received
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-sm">
                  <DialogHeader>
                    <DialogTitle>Payment Received</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddPaymentReceived} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-sm">Amount (₹)</Label>
                        <Input
                          type="number"
                          value={newPayment.amount}
                          onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })}
                          placeholder="0.00"
                          required
                          className="h-9"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm">Date</Label>
                        <Input
                          type="date"
                          value={newPayment.date}
                          onChange={(e) => setNewPayment({ ...newPayment, date: e.target.value })}
                          required
                          className="h-9"
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-sm">Payment Mode</Label>
                      <select
                        value={newPayment.payment_mode}
                        onChange={(e) => setNewPayment({ ...newPayment, payment_mode: e.target.value })}
                        className="w-full h-9 px-3 border border-slate-200 rounded-md text-sm"
                      >
                        <option value="cash">Cash</option>
                        <option value="upi">UPI</option>
                        <option value="cheque">Cheque</option>
                        <option value="bank">Bank Transfer</option>
                      </select>
                    </div>
                    <Button type="submit" className="w-full h-9 bg-green-600 hover:bg-green-700 text-white">
                      Add Payment
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Outstanding Summary with WhatsApp Reminder */}
          <Card className="p-4 border-2 border-emerald-200 bg-gradient-to-r from-emerald-50 to-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Wallet className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-emerald-600 uppercase">Total Receivable</p>
                  <p className="text-3xl font-bold text-emerald-700">
                    ₹{(customerLedger.receivable_balance || 0).toLocaleString('en-IN')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex gap-6">
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Sales</p>
                    <p className="text-lg font-semibold text-blue-600">
                      ₹{(customerLedger.total_sales || 0).toLocaleString('en-IN')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">Received</p>
                    <p className="text-lg font-semibold text-green-600">
                      ₹{(customerLedger.total_received || 0).toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>
                {customerLedger.receivable_balance > 0 && customerLedger.phone && (
                  <Button
                    onClick={() => sendWhatsAppReminder(customerLedger, customerLedger.receivable_balance)}
                    className="bg-green-500 hover:bg-green-600 text-white h-10 px-4"
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Send Reminder
                  </Button>
                )}
              </div>
            </div>
          </Card>

          {/* Ledger Table */}
          <Card className="border-slate-200 overflow-hidden">
            <div className="p-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-semibold text-slate-700">Transaction History</h3>
              <span className="text-sm text-slate-500">{customerLedger.entries_count || 0} entries</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase w-24">Date</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase">Type</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-slate-600 uppercase">Bill / Ref</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-blue-600 uppercase w-28">Sale</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-green-600 uppercase w-28">Payment</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-slate-600 uppercase w-32">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(!customerLedger.entries || customerLedger.entries.length === 0) ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-slate-500">
                        <FileText className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                        <p>No transactions yet</p>
                      </td>
                    </tr>
                  ) : (
                    customerLedger.entries.map((entry, idx) => (
                      <tr key={entry.id || idx} className="hover:bg-slate-50">
                        <td className="px-4 py-2.5 text-sm text-slate-600">
                          {entry.date ? new Date(entry.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                            entry.entry_label === 'Sale' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                          }`}>
                            {entry.entry_label === 'Sale' ? <ShoppingCart className="h-3 w-3" /> : <CreditCard className="h-3 w-3" />}
                            {entry.entry_label || 'Entry'}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-sm text-slate-600">{entry.reference || '-'}</td>
                        <td className="px-4 py-2.5 text-sm text-right font-medium text-blue-600">
                          {(entry.credit || 0) > 0 ? `₹${(entry.credit || 0).toLocaleString('en-IN')}` : '-'}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-right font-medium text-green-600">
                          {(entry.debit || 0) > 0 ? `₹${(entry.debit || 0).toLocaleString('en-IN')}` : '-'}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-right font-semibold text-emerald-600">
                          ₹{Math.abs(entry.running_balance || 0).toLocaleString('en-IN')}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </Layout>
    );
  }

  // ====== MAIN LIST VIEW ======
  return (
    <Layout>
      <div className="space-y-4 max-w-6xl mx-auto" data-testid="khata-book-container">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading tracking-tight text-slate-900">Khata Book</h1>
            <p className="text-sm text-slate-500">Manage payables & receivables</p>
          </div>
          <div className="flex items-center gap-3">
            <MonthSelector value={selectedMonth} onChange={setSelectedMonth} />
            <Button onClick={handlePrintKhatabook} variant="outline" className="h-9" title="Print (Alt+P)">
              <Printer className="h-4 w-4 mr-1.5" />Print
            </Button>
            <Button onClick={fetchData} variant="outline" className="h-9">
              <RefreshCw className="h-4 w-4 mr-1.5" />Refresh
            </Button>
          </div>
        </div>

        {/* Tabs with Search */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <div className="flex items-center gap-3">
            <TabsList className="grid grid-cols-2 w-72">
              <TabsTrigger value="payables" className="flex items-center gap-2">
                <TrendingDown className="h-4 w-4" />Payables
              </TabsTrigger>
              <TabsTrigger value="receivables" className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />Receivables
              </TabsTrigger>
            </TabsList>
            
            {/* Search */}
            {showSearch ? (
              <div className="flex items-center gap-2 flex-1 max-w-xs">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={activeTab === 'payables' ? 'Search distributors...' : 'Search customers...'}
                    className="pl-8 h-9 text-sm"
                    autoFocus
                  />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowSearch(false);
                    setSearchQuery('');
                  }}
                  className="h-9 px-2"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSearch(true)}
                className="h-9 px-3"
              >
                <Search className="h-4 w-4" />
              </Button>
            )}
          </div>

          {/* PAYABLES TAB */}
          <TabsContent value="payables" className="space-y-4">
            {/* Summary Cards - Show both cumulative and monthly */}
            <div className="grid grid-cols-4 gap-3">
              <Card className="p-4 border-red-200 bg-red-50/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <Wallet className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-red-600 uppercase">Total Payable</p>
                    <p className="text-xl font-bold text-red-700">₹{payableSummary.total.toLocaleString('en-IN')}</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 border-blue-200 bg-blue-50/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <ShoppingCart className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-blue-600 uppercase">This Month Purchases</p>
                    <p className="text-xl font-bold text-blue-700">₹{payableSummary.monthPurchases.toLocaleString('en-IN')}</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 border-green-200 bg-green-50/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CreditCard className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-green-600 uppercase">This Month Payments</p>
                    <p className="text-xl font-bold text-green-700">₹{payableSummary.monthPayments.toLocaleString('en-IN')}</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 border-slate-200 bg-slate-50/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-100 rounded-lg">
                    <TrendingDown className="h-5 w-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-600 uppercase">Net This Month</p>
                    <p className={`text-xl font-bold ${(payableSummary.monthPurchases - payableSummary.monthPayments) > 0 ? 'text-red-700' : 'text-green-700'}`}>
                      ₹{Math.abs(payableSummary.monthPurchases - payableSummary.monthPayments).toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Distributors List */}
            <Card className="border-slate-200 overflow-hidden">
              <div className="p-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-semibold text-slate-700">Distributor Ledgers</h3>
                <span className="text-xs text-slate-500">Click on any distributor to view full ledger</span>
              </div>
              <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                {distributors
                  .filter(d => d.outstanding > 0 || d.transactions_count > 0)
                  .filter(d => !searchQuery || d.name.toLowerCase().includes(searchQuery.toLowerCase()))
                  .length === 0 ? (
                  <div className="px-4 py-8 text-center text-slate-500">
                    <FileText className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                    <p>{searchQuery ? 'No matching distributors' : 'No distributors with transactions'}</p>
                  </div>
                ) : (
                  distributors
                    .filter(d => d.outstanding > 0 || d.transactions_count > 0)
                    .filter(d => !searchQuery || d.name.toLowerCase().includes(searchQuery.toLowerCase()))
                    .map((dist) => (
                    <button
                      key={dist.id}
                      onClick={() => handleDistributorClick(dist)}
                      className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-blue-50 transition-colors text-left group cursor-pointer"
                      data-testid={`distributor-${dist.id}`}
                    >
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900 group-hover:text-blue-700 transition-colors">
                          {dist.name}
                        </p>
                        <p className="text-sm text-slate-500">{dist.transactions_count} transactions</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className={`text-lg font-bold ${dist.outstanding > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            ₹{Math.abs(dist.outstanding).toLocaleString('en-IN')}
                          </p>
                          <p className="text-xs text-slate-500">{dist.outstanding > 0 ? 'Payable' : 'Clear'}</p>
                        </div>
                        <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                      </div>
                    </button>
                  ))
                )}
              </div>
            </Card>
          </TabsContent>

          {/* RECEIVABLES TAB */}
          <TabsContent value="receivables" className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-3">
              <Card className="p-4 border-emerald-200 bg-emerald-50/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <Wallet className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-emerald-600 uppercase">Total Receivable</p>
                    <p className="text-xl font-bold text-emerald-700">₹{receivableSummary.total.toLocaleString('en-IN')}</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 border-blue-200 bg-blue-50/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-blue-600 uppercase">This Month Sales</p>
                    <p className="text-xl font-bold text-blue-700">₹{receivableSummary.monthSales.toLocaleString('en-IN')}</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 border-green-200 bg-green-50/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CreditCard className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-green-600 uppercase">This Month Received</p>
                    <p className="text-xl font-bold text-green-700">₹{receivableSummary.monthReceived.toLocaleString('en-IN')}</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 border-slate-200 bg-slate-50/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-slate-100 rounded-lg">
                    <TrendingUp className="h-5 w-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-600 uppercase">Net This Month</p>
                    <p className={`text-xl font-bold ${(receivableSummary.monthSales - receivableSummary.monthReceived) > 0 ? 'text-emerald-700' : 'text-green-700'}`}>
                      ₹{Math.abs(receivableSummary.monthSales - receivableSummary.monthReceived).toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Add Customer Button + Customers List */}
            <Card className="border-slate-200 overflow-hidden">
              <div className="p-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-semibold text-slate-700">Customer Ledgers</h3>
                <Dialog open={showAddCustomer} onOpenChange={setShowAddCustomer}>
                  <DialogTrigger asChild>
                    <Button className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white">
                      <Plus className="h-4 w-4 mr-1" />Add Customer
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-sm">
                    <DialogHeader>
                      <DialogTitle>Add Customer</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddCustomer} className="space-y-3">
                      <div className="space-y-1">
                        <Label className="text-sm">Customer Name *</Label>
                        <Input
                          value={newCustomer.name}
                          onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                          placeholder="Enter name"
                          required
                          className="h-10"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-sm">Mobile Number *</Label>
                        <Input
                          type="tel"
                          value={newCustomer.phone}
                          onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                          placeholder="10-digit mobile"
                          required
                          className="h-10"
                        />
                      </div>
                      <Button type="submit" className="w-full h-10 bg-emerald-600 hover:bg-emerald-700 text-white">
                        Add Customer
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                {customers.filter(c => !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase()) || (c.phone && c.phone.includes(searchQuery))).length === 0 ? (
                  <div className="px-4 py-8 text-center text-slate-500">
                    <User className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                    <p>{searchQuery ? 'No matching customers' : 'No customers yet'}</p>
                    {!searchQuery && (
                      <Button onClick={() => setShowAddCustomer(true)} className="mt-3 bg-emerald-600 hover:bg-emerald-700 text-white">
                        Add First Customer
                      </Button>
                    )}
                  </div>
                ) : (
                  customers
                    .filter(c => !searchQuery || c.name.toLowerCase().includes(searchQuery.toLowerCase()) || (c.phone && c.phone.includes(searchQuery)))
                    .map((cust) => (
                    <button
                      key={cust.id}
                      onClick={() => handleCustomerClick(cust)}
                      className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors text-left"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{cust.name}</p>
                        <p className="text-sm text-slate-500 flex items-center gap-1">
                          <Phone className="h-3 w-3" />{cust.phone || 'No phone'}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className={`text-lg font-bold ${cust.receivable > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                            ₹{Math.abs(cust.receivable || 0).toLocaleString('en-IN')}
                          </p>
                          <p className="text-xs text-slate-500">{cust.receivable > 0 ? 'Receivable' : 'Clear'}</p>
                        </div>
                        {cust.receivable > 0 && cust.phone && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              sendWhatsAppReminder(cust, cust.receivable);
                            }}
                            className="h-8 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                          >
                            <MessageCircle className="h-4 w-4" />
                          </Button>
                        )}
                        <ChevronRight className="h-5 w-5 text-slate-400" />
                      </div>
                    </button>
                  ))
                )}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default KhataBook;
